<script setup lang="ts">
import emptyUrl from '@/assets/images/emptyAvatar.png';
import { ref, watchEffect, onMounted } from 'vue';
import api from '~/api';
import appConfig from '~/app.config';

const SIZE = 56;
const props = defineProps({
    leftSrc: {
        type: String,
        required: true
    },
    leftCoord: {
        type: Object,
        required: false,
        default: null,
        validator(value) {
            if (value === null) return true;
            return 'x' in value && 'y' in value && 'width' in value && 'height' in value;
        }
    },
    rightSrc: {
        type: String,
        default: ''
    },
    deletable: {
        type: Boolean,
        default: false
    },
    enableUpload: {
        type: Boolean,
        default: true
    }
});

const emit = defineEmits(['update:rightSrc', 'faceUploaded']);
const rightSrc = ref(props.rightSrc);
const imageWidth = ref(0);
const imageHeight = ref(0);
const fileInputRef = ref<HTMLInputElement | null>(null);
const isUploading = ref(false);
const snackbar = useSnackbarStore();
const globalLoading = useLoadingStore();
const utils = useUtils();
const { $logError } = useNuxtApp();

watchEffect(() => {
    rightSrc.value = props.rightSrc;
});

const removeRightSrc = () => {
    rightSrc.value = ''; // 清空右侧图片
    emit('update:rightSrc', '');
};

// 触发文件选择
const triggerFileUpload = () => {
    if (!props.enableUpload || isUploading.value) return;
    fileInputRef.value?.click();
};

// 处理文件上传
const handleFileChange = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
        const file = target.files[0];
        await validateAndUploadFile(file);
        target.value = ''; // 清空 input 的值
    }
};

// 验证并上传文件
const validateAndUploadFile = async (file: File) => {
    const validImageTypes = appConfig.imageExt
        .filter((format) => format.ext !== 'gif')
        .map((format) => format.mime);

    const supportedExt = appConfig.imageExt
        .filter((format) => format.ext !== 'gif')
        .map((format) => format.ext);

    if (!validImageTypes.includes(file.type)) {
        const errorMessage = `Unsupported file type. Only ${supportedExt.join(',')} are allowed.`;
        snackbar.showError(errorMessage);
        utils.reportError('Face文件类型验证失败', 'face type validation error', {
            fileType: file.type,
            supportedTypes: validImageTypes
        });
        return;
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
        snackbar.showError('File size exceeds 10MB.');
        utils.reportError('Face文件大小验证失败', 'face size validation error', {
            fileSize: file.size,
            maxSize
        });
        return;
    }

    try {
        isUploading.value = true;
        globalLoading.startLoading('Uploading face...');

        const result = await api.uploadFace(file);

        if (result && result.url) {
            rightSrc.value = result.url;
            emit('update:rightSrc', result.url);
            emit('faceUploaded', {
                url: result.url,
                faceId: result.face_id
            });
            snackbar.showSuccess('Face uploaded successfully!');
        }
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || 'Upload failed';
        snackbar.showError(errorMessage);
        $logError('上传face失败', 'upload face error', {
            error: errorMessage,
            fileName: file.name
        });
    } finally {
        isUploading.value = false;
        globalLoading.stopLoading();
    }
};

// 动态获取图片宽高
onMounted(() => {
    const img = new Image();
    img.onload = () => {
        imageWidth.value = img.naturalWidth;
        imageHeight.value = img.naturalHeight;
    };
    img.src = props.leftSrc;
});
</script>

<template>
    <div class="avatar-couple flex relative left-1">
        <!-- 新格式：coord 为 null，直接显示人脸图片 -->
        <div v-if="!props.leftCoord" :style="{
            backgroundImage: `url(${props.leftSrc})`,
        }" class="avatar rounded-full p-2 w-14 h-14 border-2 !border-white overflow-hidden bg-center bg-cover">
        </div>
        <!-- 旧格式：使用坐标裁剪 -->
        <div v-else-if="imageWidth && imageHeight" :style="{
            backgroundImage: `url(${props.leftSrc})`,
            backgroundSize: `${(imageWidth / props.leftCoord.width) * SIZE}px ${(imageHeight / props.leftCoord.height) * SIZE}px !important`,
            backgroundPosition: `-${(props.leftCoord.x / props.leftCoord.width) * SIZE}px -${(props.leftCoord.y / props.leftCoord.height) * SIZE}px !important`,
        }" class="avatar rounded-full p-2 w-14 h-14  border-2 !border-white overflow-hidden bg-center bg-cover">
        </div>
        <div
            :style="{ backgroundImage: `url(${rightSrc || emptyUrl})` }"
            class="avatar rounded-full w-14 h-14 overflow-visible bg-center bg-cover relative right-2 right-avatar"
            :class="{
                'border-2 !border-orange-500': !!rightSrc,
                'cursor-pointer hover:opacity-80 transition-opacity': enableUpload && !rightSrc && !isUploading
            }"
            @click="!rightSrc && enableUpload ? triggerFileUpload() : null"
        >
            <!-- 上传图标 - 仅在启用上传且没有选择图片时显示 -->
            <div
                v-if="enableUpload && !rightSrc && !isUploading"
                class="absolute inset-0 flex items-center justify-center bg-black-100 rounded-full"
            >
                <v-icon size="24" color="grey-darken-1">mdi-cloud-upload</v-icon>
            </div>

            <!-- 上传中状态 -->
            <div
                v-if="isUploading"
                class="absolute inset-0 flex items-center justify-center bg-black-100 rounded-full"
            >
                <v-progress-circular
                    indeterminate
                    color="primary"
                    size="24"
                ></v-progress-circular>
            </div>

            <!-- 删除图标 -->
            <transition name="fade" v-if="deletable">
                <img v-if="rightSrc" class="absolute -top-1 -right-1 w-5 z-10 invisible delete-icon cursor-pointer" src="~/assets/images/delete.png" alt="" @click.stop="removeRightSrc">
            </transition>
        </div>

        <!-- 隐藏的文件输入 -->
        <input
            v-if="enableUpload"
            ref="fileInputRef"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            class="hidden"
            @change="handleFileChange"
        />
    </div>
</template>

<style scoped>
.avatar {
    background-repeat: no-repeat;
}

.right-avatar:hover .delete-icon {
    visibility: visible !important;
}
</style>
